// src/modules/units/tyres/views/available-sensors.api.js
//
// Candidate sensors for tyre Fit / Move.
//
// GET /api/customer/{customerId}/sensor is the inventory authority here. Unlike
// global.units it also contains sensors that have completed assembly but have not
// yet reported through a truck/trailer gateway, so a brand-new sensor can be
// selected immediately after it is assigned to the customer.
//
// Important availability rules:
//   - wheel/tyreId position fields from this endpoint are deliberately ignored;
//     they may describe the sensor's PREVIOUS wheel position.
//   - tyre Application never participates in filtering; a sensor can be used for
//     any tyre application.
//   - any sensor that still reports an active tyre association is excluded.
//   - global.units is used only as a defensive live cross-check: a real sensor
//     currently present in wheelSensors/spareSensors is mounted on a vehicle and
//     must not be offered even if the inventory record is briefly stale.
//
// The result is paged with explicit positive limits because this endpoint defaults
// to 20 rows.

import { getUserData } from '../../../auth/views/login/auth.api.js';
import global from '../../../../shared/utils/global.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';
const SENSOR_PAGE_SIZE = 200;
const SENSOR_MAX_PAGES = 60;

const isBlank = (v) => v === null || v === undefined || v === '';
const keyOf = (v) => String(v ?? '').trim();

function currentCustomerId() {
  return global?.customerId || getUserData()?.parentCustomerId || null;
}

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    credentials: 'include'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      message: data?.message || data?.error || 'Sensor request failed.',
      data
    };
  }

  return { success: true, status: response.status, data };
}

function sensorIdOf(row) {
  return row?.Id ?? row?.sensor?.Id ?? row?.sensorId ?? row?.sensor?.sensorId ?? null;
}

// These are the live arrays that represent a sensor physically occupying a wheel
// position. Placeholder ids generated from repeater configuration do not match
// real backend sensor ids, so they are harmless here.
function mountedSensorIds() {
  const ids = new Set();

  (global?.units || []).forEach((unit) => {
    [...(unit?.wheelSensors || []), ...(unit?.spareSensors || [])].forEach((sensor) => {
      const id = sensor?.Id ?? sensor?.sensorId ?? sensor?.id;
      const key = keyOf(id);
      if (key) ids.add(key);
    });
  });

  return ids;
}

function hasActiveTyreAssociation(row, sensorId) {
  const sensor = row?.sensor || {};
  const tyre = row?.tyre || sensor?.tyre || null;

  // The sensor-side externalTyreId is the strongest simple signal that the
  // sensor is already allocated to a tyre. Do NOT inspect tyreId: on sensor
  // records that field is used as a wheel-position value and may be historical.
  const externalTyreId = row?.externalTyreId ?? sensor?.externalTyreId;
  if (!isBlank(externalTyreId)) return true;

  // If a nested tyre is present, only treat it as active when its currentSensorId
  // points at this sensor. That prevents an unrelated/historical tyre payload from
  // blocking a genuinely free sensor.
  const currentSensorId = tyre?.currentSensorId;
  if (!isBlank(currentSensorId) && keyOf(currentSensorId) === keyOf(sensorId)) return true;

  return false;
}

function toCandidate(row) {
  const id = sensorIdOf(row);
  if (isBlank(id)) return null;

  return {
    id: keyOf(id),
    assembly: row?.assembly || null,
    customerId: row?.customerId ?? row?.sensor?.customerId ?? null,
    customerName: row?.customerName ?? null,
    source: row
  };
}

/**
 * Return every sensor that can currently be selected for Fit / Move.
 *
 * `inclChild` defaults true so the tyre manager follows the same hierarchy-wide
 * scope as its yard/unit lists. Pass false if a caller needs only the exact active
 * customer later.
 */
export async function fetchAvailableSensors({ inclChild = true, signal } = {}) {
  const customerId = currentCustomerId();
  if (!customerId) {
    return { success: false, status: 0, message: 'No customer in session.', data: null };
  }

  const rows = [];
  let status = 200;
  let count = NaN;
  let offset = 0;

  for (let page = 0; page < SENSOR_MAX_PAGES; page++) {
    const query = new URLSearchParams({
      inclChild: String(inclChild),
      limit: String(SENSOR_PAGE_SIZE),
      offset: String(offset)
    });

    const res = await request(
      `/customer/${encodeURIComponent(customerId)}/sensor?${query.toString()}`,
      { method: 'GET', signal }
    );

    if (!res.success) return res;

    status = res.status;

    const batch = Array.isArray(res.data?.results)
      ? res.data.results
      : [];

    count = Number(res.data?.count);

    console.log('SENSOR PAGE', {
      page,
      offset,
      requestedLimit: SENSOR_PAGE_SIZE,
      backendCount: count,
      returnedThisPage: batch.length
    });

    // Nothing more came back
    if (batch.length === 0) {
      break;
    }

    rows.push(...batch);

    // Move forward by the amount the backend ACTUALLY returned
    offset += batch.length;

    // We have fetched everything the backend says exists
    if (
      Number.isFinite(count) &&
      rows.length >= count
    ) {
      break;
    }
  }

  const mounted = mountedSensorIds();
  const byId = new Map();

  rows.forEach((row) => {
    const candidate = toCandidate(row);
    if (!candidate) return;
    if (mounted.has(candidate.id)) return;
    if (hasActiveTyreAssociation(row, candidate.id)) return;
    if (!byId.has(candidate.id)) byId.set(candidate.id, candidate);
  });

  const results = [...byId.values()].sort((a, b) =>
    a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' })
  );

  return {
    success: true,
    status,
    data: {
      count: results.length,
      results
    }
  };
}
